import type { ReactElement, ReactNode } from 'react';
import { ActionIcon, Group, Select, Text, TextInput, Tooltip } from '@mantine/core';
import { Download, Rows2, Rows3, Search } from 'lucide-react';
import type { MegaGridColumn, MegaGridFacet, MegaGridGroupByPill } from './MegaGridTypes';
import type { MegaGridController } from './useMegaGridState';
import MegaGridColumnSelector from './MegaGridColumnSelector';
import MegaGridGroupByEditor from './MegaGridGroupByEditor';
import MegaGridGroupByPills from './MegaGridGroupByPills';
import MegaGridSavedViews from './MegaGridSavedViews';

interface MegaGridToolbarProps<T> {
    controller: MegaGridController;
    columns: MegaGridColumn<T>[];
    groupableColumns: MegaGridColumn<T>[];
    /** When provided, the group-by popover editor is replaced by a flat row of these pills. */
    groupByPills?: MegaGridGroupByPill[];
    hasSearch: boolean;
    searchPlaceholder: string;
    facets: MegaGridFacet<T>[];
    /** The filter cluster ("Filters (n)" chip + Add-filter menu), rendered beside the group control. */
    filterControls?: ReactNode;
    customToolbar?: ReactNode;
    countLabel: string;
    enableColumnSelector: boolean;
    enableGrouping: boolean;
    enableSavedViews: boolean;
    allowPinning: boolean;
    onExport?: () => void;
}

export default function MegaGridToolbar<T>(props: MegaGridToolbarProps<T>): ReactElement {
    const { controller, columns, groupableColumns, hasSearch, searchPlaceholder, facets, filterControls, customToolbar, countLabel } = props;
    const { state } = controller;

    return (
        <Group justify="space-between" px="md" py="sm" wrap="wrap" style={{ borderBottom: '1px solid var(--bm-color-border)' }}>
            <Group gap="sm" wrap="wrap">
                {hasSearch && (
                    <TextInput
                        leftSection={<Search size={16} />}
                        placeholder={searchPlaceholder}
                        value={state.search}
                        onChange={(e) => controller.setSearch(e.currentTarget.value)}
                        w={260}
                        aria-label={searchPlaceholder}
                    />
                )}
                {facets.map((f) => (
                    <Select
                        key={f.key}
                        placeholder={f.placeholder}
                        aria-label={f.placeholder}
                        data={[{ label: f.allLabel, value: 'all' }, ...f.options]}
                        value={state.facets[f.key] ?? 'all'}
                        onChange={(val) => controller.setFacet(f.key, val ?? 'all')}
                        w={f.width ?? 180}
                        allowDeselect={false}
                    />
                ))}
                {props.enableGrouping && props.groupByPills !== undefined ? (
                    <MegaGridGroupByPills
                        pills={props.groupByPills}
                        groupBy={state.groupBy}
                        onAdd={controller.addGroupBy}
                        onRemove={controller.removeGroupBy}
                        onClear={controller.clearGroupBy}
                    />
                ) : (
                    props.enableGrouping &&
                    groupableColumns.length > 0 && (
                        <MegaGridGroupByEditor
                            groupable={groupableColumns}
                            groupBy={state.groupBy}
                            onAdd={controller.addGroupBy}
                            onRemove={controller.removeGroupBy}
                            onReorder={controller.reorderGroupBy}
                            onSetSort={controller.setGroupSort}
                            onClear={controller.clearGroupBy}
                        />
                    )
                )}
                {filterControls}
                {customToolbar}
            </Group>

            <Group gap="sm" wrap="wrap">
                <Text size="sm" c="dimmed">
                    {countLabel}
                </Text>
                <Tooltip label={state.density === 'compact' ? 'Comfortable rows' : 'Compact rows'} withArrow>
                    <ActionIcon
                        variant="default"
                        onClick={() => controller.setDensity(state.density === 'compact' ? 'comfortable' : 'compact')}
                        aria-label="Toggle density"
                    >
                        {state.density === 'compact' ? <Rows3 size={16} /> : <Rows2 size={16} />}
                    </ActionIcon>
                </Tooltip>
                {props.onExport && (
                    <Tooltip label="Export CSV" withArrow>
                        <ActionIcon variant="default" onClick={props.onExport} aria-label="Export CSV">
                            <Download size={16} />
                        </ActionIcon>
                    </Tooltip>
                )}
                {props.enableColumnSelector && (
                    <MegaGridColumnSelector
                        columns={columns}
                        order={state.columnOrder}
                        hidden={state.hidden}
                        pinned={state.pinned}
                        allowPinning={props.allowPinning}
                        onToggleVisible={controller.setColumnVisible}
                        onReorder={controller.reorderColumns}
                        onTogglePin={controller.togglePin}
                    />
                )}
                {props.enableSavedViews && (
                    <MegaGridSavedViews
                        views={controller.views}
                        activeViewId={controller.activeViewId}
                        onApply={controller.applyView}
                        onSave={controller.saveView}
                        onRename={controller.renameView}
                        onDelete={controller.deleteView}
                        onTogglePin={controller.togglePinView}
                        onReset={controller.resetView}
                    />
                )}
            </Group>
        </Group>
    );
}
